<template>
    <div class="model-page-image-upload">
        <div
            class="upload-area relative"
            :class="{
                'has-files': uploadedFiles.length > 0,
                'drag-over': isDragOver,
                disabled: isUploading,
            }"
            @click="triggerFileInput"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
        >
            <div
                v-if="isUploading"
                class="absolute z-[100] bg-black/50 rounded-[8px] top-0 left-0 w-full h-full flex items-center justify-center"
            >
                <l-dot-spinner
                    v-if="isClient"
                    size="40"
                    speed="0.9"
                    color="var(--el-menu-active-color)"
                ></l-dot-spinner>
            </div>

            <input
                ref="fileInputRef"
                type="file"
                :accept="acceptTypes"
                :multiple="allowMultiple"
                style="display: none"
                @change="handleFileChange"
            />

            <div v-if="uploadedFiles.length === 0" class="upload-placeholder">
                <div class="upload-icon">
                    <!-- <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="8" y="12" width="32" height="24" rx="4" stroke="currentColor" stroke-width="2" fill="none"/>
                        <path d="M20 20l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <circle cx="32" cy="20" r="2" fill="currentColor"/>
                    </svg> -->
                    <img
                        src="https://static.weryai.com/cms/c3b415bfabc275771e6bb29c35cacf07.webp"
                        alt="Upload image icon"
                        class="upload-img"
                        width="36"
                        height="36"
                        loading="lazy"
                    />
                </div>
                <p class="upload-text">{{ t('upload.clickOrDragImages') }}</p>
                <!-- <p class="upload-hint">{{ t('upload.supportedImageFormats') }}</p>
                <p class="upload-constraints">
                    {{ t('upload.imageConstraints', { maxSize: maxSizeMB, maxResolution: maxResolution }) }}
                </p> -->
            </div>

            <div v-else class="uploaded-files">
                <div
                    v-for="(file, index) in uploadedFiles"
                    :key="file.id"
                    class="file-item"
                >
                    <div class="file-preview">
                        <img
                            :src="file.previewUrl"
                            :alt="file.name"
                            class="preview-image"
                        />
                        <div class="file-info">
                            <span class="file-name">{{ file.name }}</span>
                            <span class="file-size">{{
                                formatFileSize(file.size)
                            }}</span>
                        </div>
                    </div>
                    <button
                        :disabled="isUploading"
                        class="remove-btn"
                        @click.stop="removeFile(index)"
                    >
                        ×
                    </button>
                </div>

                <div
                    v-if="allowMultiple"
                    class="add-more-btn"
                    @click.stop="triggerFileInput"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 5v14M5 12h14"
                            stroke="#4A9EFF"
                            stroke-width="2"
                            stroke-linecap="round"
                        />
                    </svg>
                    <span>{{ t('upload.addMore') }}</span>
                </div>
            </div>
        </div>

        <div v-if="errorMessage" class="error-message">
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="8" cy="8" r="7" stroke="#ef4444" stroke-width="2" />
                <path
                    d="M8 4v4M8 12h.01"
                    stroke="#ef4444"
                    stroke-width="2"
                    stroke-linecap="round"
                />
            </svg>
            <span>{{ errorMessage }}</span>
        </div>
        <p class="upload-hint upload-detail">
            {{ t('upload.supportedImageFormats') }}
            {{
                t('upload.imageConstraints', {
                    maxSize: maxSizeMB,
                    maxResolution: maxResolution,
                })
            }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useUserStore } from '~/stores/user'
import { showLoginModal } from '~/utils/emitter'
import { showNotification, getMaxImageSize } from '@/utils/common'

// 类型定义
interface UploadedFile {
    id: string
    file: File
    name: string
    size: number
    previewUrl: string
    uploadStatus: 'pending' | 'uploading' | 'success' | 'error'
    uploadResult?: unknown
    errorMessage?: string
}

// Props
const props = withDefaults(
    defineProps<{
        maxSizeMB?: number
        maxResolution?: number
        allowMultiple?: boolean
        acceptTypes?: string
    }>(),
    {
        maxSizeMB: 10,
        maxResolution: 4096,
        allowMultiple: false,
        acceptTypes: 'image/jpeg,image/png,image/gif,image/webp,image/jpg',
    }
)

// Emits
const emit = defineEmits<{
    'upload-success': [files: UploadedFile[]]
    'upload-error': [error: string]
    'files-change': [files: UploadedFile[]]
}>()

// Composables
const { t, locale } = useI18n()
const userStore = useUserStore()

// Refs
const fileInputRef = ref<HTMLInputElement>()
const uploadedFiles = ref<UploadedFile[]>([])
const isDragOver = ref(false)
const isUploading = ref(false)
const errorMessage = ref('')

// Computed
const isLoggedIn = computed(() => userStore.isLoggedIn)

// 生成唯一ID
const generateId = () => Math.random().toString(36).substr(2, 9)

// 格式化文件大小
const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 验证图片文件
const validateImageFile = (file: File): string | null => {
    // 验证文件类型
    const validTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/jpg',
    ]
    if (!validTypes.includes(file.type)) {
        return t('upload.errors.invalidImageType')
    }
    console.log(props.maxSizeMB)

    // 验证文件大小（使用统一的 getMaxImageSize 函数，限制为10MB）
    if (!getMaxImageSize(file, props.maxSizeMB)) {
        return t('upload.errors.fileTooLarge', { maxSize: props.maxSizeMB })
    }

    return null
}

// 验证图片分辨率
const validateImageResolution = (file: File): Promise<string | null> => {
    return new Promise(resolve => {
        const img = new Image()
        img.onload = () => {
            if (
                img.width > props.maxResolution ||
                img.height > props.maxResolution
            ) {
                resolve(
                    t('upload.errors.resolutionTooLarge', {
                        maxResolution: props.maxResolution,
                    })
                )
            } else {
                resolve(null)
            }
        }
        img.onerror = () => {
            resolve(t('upload.errors.invalidImageFile'))
        }
        img.src = URL.createObjectURL(file)
    })
}

// 处理文件选择
const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement
    const files = Array.from(target.files || [])

    await processFiles(files)
}

// 处理拖拽
const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    isDragOver.value = true
}

const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    isDragOver.value = false
}

const handleDrop = async (event: DragEvent) => {
    event.preventDefault()
    isDragOver.value = false

    const files = Array.from(event.dataTransfer?.files || [])

    // 检查登录状态
    if (!isLoggedIn.value) {
        showLoginModal('file-upload', () => {
            // 登录成功后，不自动处理文件（避免浏览器安全限制）
            // 用户需要重新拖拽文件
        })
        return
    }

    // 已登录，正常处理文件
    await processFiles(files)
}

// 处理文件
const processFiles = async (files: File[]) => {
    errorMessage.value = ''

    // 检查是否支持多文件
    if (!props.allowMultiple && files.length > 1) {
        errorMessage.value = t('upload.errors.multipleFilesNotAllowed')
        return
    }

    // 检查是否已有文件且不支持多文件
    if (!props.allowMultiple && uploadedFiles.value.length > 0) {
        errorMessage.value = t('upload.errors.replaceExistingFile')
        return
    }

    const validFiles: UploadedFile[] = []

    for (const file of files) {
        // 基础验证
        const basicError = validateImageFile(file)
        if (basicError) {
            errorMessage.value = basicError
            continue
        }

        // 分辨率验证
        const resolutionError = await validateImageResolution(file)
        if (resolutionError) {
            errorMessage.value = resolutionError
            continue
        }

        // 创建文件对象
        const uploadedFile: UploadedFile = {
            id: generateId(),
            file,
            name: file.name,
            size: file.size,
            previewUrl: URL.createObjectURL(file),
            uploadStatus: 'pending',
        }

        validFiles.push(uploadedFile)
    }

    if (validFiles.length > 0) {
        if (props.allowMultiple) {
            uploadedFiles.value.push(...validFiles)
        } else {
            uploadedFiles.value = validFiles
        }

        emit('files-change', uploadedFiles.value)

        // 开始上传（登录状态已在更早阶段检查）
        await uploadFiles(validFiles)
    }
}

// 上传文件
const uploadFiles = async (files: UploadedFile[]) => {
    isUploading.value = true

    try {
        for (const fileItem of files) {
            fileItem.uploadStatus = 'uploading'

            try {
                // 生成批次号（可选）
                const batchNo = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

                // 创建 FormData
                const formData = new FormData()
                formData.append('file', fileItem.file)

                // 获取配置和 token
                const config = useRuntimeConfig()
                const token = import.meta.client
                    ? localStorage.getItem('token') || ''
                    : ''

                // 构建请求头
                // 请求语言跟随当前 URL 的 locale（/zh/... => zh），不要依赖偏好/旧 localStorage('language')
                const headers: Record<string, string> = {
                    'Accept-Language': locale.value || 'en',
                }

                // 添加 Authorization 头部
                if (token) {
                    headers['Authorization'] = token
                }

                // 使用 $fetch 进行上传
                const result = await $fetch('/api/v1/auth/s3/upload_image', {
                    baseURL: config.public.apiBase,
                    method: 'POST',
                    body: formData,
                    headers,
                    params: {
                        batch_no: batchNo,
                        app_key: '20006012',
                        lang: headers['Accept-Language'],
                    },
                    credentials: token ? 'omit' : 'include',
                })

                fileItem.uploadStatus = 'success'
                fileItem.uploadResult = result
            } catch (error) {
                fileItem.uploadStatus = 'error'
                fileItem.errorMessage =
                    error instanceof Error ? error.message : 'Upload failed'
            }
        }

        // 检查是否有成功的上传
        const successFiles = files.filter(f => f.uploadStatus === 'success')

        if (successFiles.length > 0) {
            emit('upload-success', successFiles)
        }

        // 检查是否有失败的上传
        const errorFiles = files.filter(f => f.uploadStatus === 'error')
        if (errorFiles.length > 0) {
            emit('upload-error', t('upload.errors.uploadFailed'))
            showNotification.error(t('upload.errors.uploadFailed'))
        }
    } finally {
        isUploading.value = false
    }
}

// 触发文件选择
const triggerFileInput = () => {
    if (isUploading.value) return

    // 检查登录状态
    if (!isLoggedIn.value) {
        showLoginModal('file-upload', () => {
            // 登录成功后，不自动触发文件选择器（避免浏览器安全限制）
            // 用户需要再次点击上传区域来触发文件选择
        })
        return
    }

    // 已登录，正常触发文件选择
    if (fileInputRef.value) {
        fileInputRef.value.click()
    }
}

// 移除文件
const removeFile = (index: number) => {
    if (!isUploading.value) {
        const file = uploadedFiles.value[index]
        if (file) {
            URL.revokeObjectURL(file.previewUrl)
            uploadedFiles.value.splice(index, 1)
            emit('files-change', uploadedFiles.value)
        }
    }
}

// 监听登录状态变化
watch(isLoggedIn, newValue => {
    if (
        newValue &&
        uploadedFiles.value.some(f => f.uploadStatus === 'pending')
    ) {
        // 用户登录后，上传待处理的文件
        const pendingFiles = uploadedFiles.value.filter(
            f => f.uploadStatus === 'pending'
        )
        uploadFiles(pendingFiles)
    }
})

// 清理资源
const cleanup = () => {
    uploadedFiles.value.forEach(file => {
        URL.revokeObjectURL(file.previewUrl)
    })
}

const isClient = ref(false)

// 只在客户端注册 ldrs
onMounted(async () => {
    if (typeof window !== 'undefined') {
        try {
            const { dotSpinner } = await import('ldrs')
            dotSpinner.register()
            // 确保注册完成后再设置 isClient，避免组件在注册前渲染
            isClient.value = true
        } catch (error) {
            console.error('Failed to load ldrs:', error)
            // 即使加载失败也设置为 true，避免无限等待
            isClient.value = true
        }
    }
})
// 组件卸载时清理
onUnmounted(() => {
    cleanup()
})
</script>

<style lang="less" scoped>
.model-page-image-upload {
    width: 100%;
}

.upload-area {
    // border: 2px dashed rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 3rem 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    /* background: rgba(255, 255, 255, 0.02); */
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #333643;

    &:hover {
        border-color: rgba(255, 255, 255, 0.4);
        background: rgba(255, 255, 255, 0.05);
    }

    &.has-files {
        padding: 1rem;
        border-style: solid;
        background: rgba(255, 255, 255, 0.02);
        min-height: auto;
    }

    &.drag-over {
        border-color: rgba(255, 255, 255, 0.6);
        background: rgba(255, 255, 255, 0.08);
        transform: scale(1.01);
    }

    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;

    .upload-icon {
        margin-bottom: 0;

        svg {
            width: 48px;
            height: 48px;
            opacity: 0.6;
        }
        .upload-img {
            width: 36px;
            height: 36px;
        }
    }

    .upload-text {
        font-size: 1rem;
        font-weight: 500;
        /* color: rgba(255, 255, 255, 0.8); */
        color: #999;
        margin-bottom: 0;
        display: block;
    }

    .upload-hint {
        font-size: 0.875rem;
        color: rgba(255, 255, 255, 0.6);
        margin-bottom: 0;
    }

    .upload-constraints {
        font-size: 0.75rem;
        color: rgba(255, 255, 255, 0.5);
        margin-top: 0.5rem;
        line-height: 1.4;
    }
}

.uploaded-files {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1rem;
}

.file-item {
    position: relative;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;

    &:hover {
        border-color: rgba(255, 255, 255, 0.2);
        background: rgba(255, 255, 255, 0.08);
    }

    .file-preview {
        padding: 0.75rem;

        .preview-image {
            width: 100%;
            height: 120px;
            object-fit: cover;
            border-radius: 4px;
            margin-bottom: 0.5rem;
        }

        .file-info {
            .file-name {
                font-size: 0.875rem;
                font-weight: 500;
                color: rgba(255, 255, 255, 0.9);
                display: block;
                margin-bottom: 0.25rem;
                word-break: break-all;
            }

            .file-size {
                font-size: 0.75rem;
                color: rgba(255, 255, 255, 0.6);
            }
        }
    }

    .remove-btn {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: rgba(239, 68, 68, 0.9);
        color: white;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        font-weight: bold;
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
            background: #ef4444;
            transform: scale(1.1);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
}

.add-more-btn {
    border: 2px dashed rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.02);
    min-height: 120px;

    &:hover {
        border-color: rgba(255, 255, 255, 0.4);
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.05);
    }

    span {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        font-weight: 500;
    }
}

.upload-status {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1rem;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.1);
    gap: 1rem;
    .status-icon {
        margin-right: 0.5rem;
    }
}

.error-message {
    display: flex;
    align-items: center;
    margin-top: 1rem;
    padding: 0.75rem;
    background: rgba(239, 68, 68, 0.1);
    border-radius: 8px;
    color: #ef4444;
    font-size: 0.875rem;
    border: 1px solid rgba(239, 68, 68, 0.2);

    svg {
        margin-right: 0.5rem;
        flex-shrink: 0;
    }
}

.upload-detail {
    font-size: 0.75rem;
    font-weight: 400;
    margin-top: 5px;
    color: #adadad;
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
